import {
  Connector,
  useConnect,
  useSignMessage,
  useConnectors,
  useConnection,
} from "wagmi";
import { trackEvent } from "contexts/mixpanel";
import type { AuthWalletConnection } from "../types";

/**
 * Wallet Signature Message
 *
 * Signing this message proves ownership of the wallet address
 */
const SIGNATURE_MESSAGE =
  "Signing this message proves ownership of my wallet address to https://hug.art and represents an agreement to our terms and conditions.";

/**
 * Show a user-friendly error if something goes wrong during connection or signing
 */
const decodeWalletError = (error: Error | null): string | null => {
  if (!error) {
    return null;
  }

  // User declined connecting with their selected wallet or didn't submit their password.
  // Don't show error message in case they just want to connect with a different wallet.
  if (
    /user rejected the request/i.test(error.message) || // metamask
    /user denied account authorization/i.test(error.message) || // coinbase
    /connection request reset/i.test(error.message) || // wallet connect - closed modal
    /user disapproved requested chains/i.test(error.message) // wallet connect - declined after scanning code
  ) {
    return null;
  }

  if (/user denied message signature/i.test(error.message)) {
    return "Looks like you rejected the signature request. We need that to verify you own the wallet. Please retry to continue.";
  }

  // eslint-disable-next-line
  console.error("[ConnectWallet]", error);
  return "Something went wrong, reload the page and try again.";
};

/**
 * Handler that runs after successful wallet connection
 */
type ConnectHandler = (
  connection: AuthWalletConnection,
) => void | Promise<void>;

type UseConnectWalletOptions = {
  onConnect?: ConnectHandler;
};

/**
 * Custom hook for wallet connection and message signing
 *
 * Handles the full flow of:
 * 1. Connecting to a wallet via connector
 * 2. Signing a message to prove ownership
 * 3. Calling the onConnect callback with the connection details
 */
const useConnectWallet = ({ onConnect }: UseConnectWalletOptions = {}) => {
  const connectMutation = useConnect();

  const connectors = useConnectors();

  const signMessageMutation = useSignMessage();

  const { address, isConnected, connector: activeConnector } = useConnection();

  /**
   * Connect to a wallet and sign message to prove ownership
   */
  const connectAndSign = async (connector: Connector) => {
    try {
      // Log sign in attempt in analytics
      trackEvent({
        name: "Button Click",
        ButtonName: "Connect Wallet",
        ButtonContext: connector.name,
      });

      // Attempt to connect to Wallet
      const connection = await connectMutation.mutateAsync({ connector });

      // Sign message to prove ownership
      const signature = await signMessageMutation.mutateAsync({
        message: SIGNATURE_MESSAGE,
      });

      // Run given handler on success
      onConnect?.({
        walletAddress: connection.accounts[0],
        signature,
        chainId: connection.chainId,
      });
    } catch (e) {
      // eslint-disable-next-line
      console.log(e);
    }
  };

  /**
   * Re-sign message for already connected wallet
   *
   * Used when user declined to sign message previously, or it failed
   */
  const signMessage = async () => {
    try {
      const signature = await signMessageMutation.mutateAsync({
        message: SIGNATURE_MESSAGE,
      });

      if (onConnect && address && activeConnector) {
        onConnect({
          walletAddress: address,
          signature,
          chainId: await activeConnector.getChainId(),
        });
      }
    } catch (e) {
      // eslint-disable-next-line
      console.log(e);
    }
  };

  const error = decodeWalletError(
    connectMutation.error ?? signMessageMutation.error,
  );

  return {
    // State
    connectors,
    error,
    isConnecting: connectMutation.isPending,
    isSigning: signMessageMutation.isPending,
    isConnected,
    address,
    activeConnector,
    // Actions
    connectAndSign,
    signMessage,
  };
};

export type { ConnectHandler };
export { useConnectWallet, SIGNATURE_MESSAGE, decodeWalletError };
